import { FC, useState, useContext, HTMLAttributes } from "react";

import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { Dropzone } from "@mantine/dropzone";
import { GrFormClose } from "react-icons/gr";
import { Box, Text, Group, ActionIcon } from "@mantine/core";
import { BiLinkExternal, BiPhotoAlbum } from "react-icons/bi";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";

import { AuthContext } from "@components/AuthProvider/contexts/Auth.context";

export const FileUpload: FC<
  HTMLAttributes<HTMLDivElement> & {
    url?: string;
    onUrl?: (url: string) => void;
  }
> = ({ url, onUrl = () => {}, ...props }) => {
  const { t } = useTranslation();

  const { authUser } = useContext(AuthContext)!;

  const [isUploading, setIsUploading] = useState(false);

  const handleFile = async (file: File) => {
    setIsUploading(true);

    const store = ref(
      getStorage(),
      `${authUser.uid}/${dayjs().valueOf()}_${file.name}`,
    );
    const arrayBuffer = await file.arrayBuffer();

    const upload = await uploadBytes(store, arrayBuffer, {
      contentType: file.type,
    });
    const downloadUrl = await getDownloadURL(upload.ref);

    onUrl(downloadUrl);

    setIsUploading(false);
  };
  return (
    <Box sx={{ position: "relative" }}>
      <Dropzone
        // @ts-ignore
        onDrop={(files) => handleFile(files[0])}
        onReject={(files) => console.log("rejected files", files)}
        maxSize={3 * 1024 ** 2}
        accept={["image/*"]}
        loading={isUploading}
      >
        <Group
          position="center"
          spacing="xl"
          noWrap
          maw="80%"
          sx={{ marginInline: "auto" }}
          style={{ minHeight: 220, pointerEvents: "none" }}
        >
          {url ? (
            <img
              alt=""
              style={{
                width: "200px !important",
                height: "200px !important",
                maxWidth: "100%",
              }}
              src={url}
            />
          ) : (
            <>
              <Dropzone.Idle>
                <BiPhotoAlbum size={50} style={{ flexShrink: 0 }} />
              </Dropzone.Idle>
              <div>
                <Text size="xl" inline>
                  {t("Drag image here or click to select file.")}
                </Text>
                <Text size="sm" color="dimmed" inline mt={7}>
                  {t(
                    "Photos only, no advertisements, logos, posters or flyers!",
                  )}
                </Text>
                <Text size="xs" color="dimmed" inline mt={7}>
                  {t("imageUploader.dimensions")}
                </Text>
              </div>
            </>
          )}
        </Group>
      </Dropzone>
      {url && (
        <Group
          spacing="xs"
          sx={(t) => ({
            position: "absolute",
            top: t.spacing.md,
            right: t.spacing.md,
          })}
        >
          <ActionIcon
            variant="filled"
            onClick={() => {
              window.open(url, "_blank");
            }}
          >
            <BiLinkExternal />
          </ActionIcon>
          <ActionIcon
            variant="filled"
            sx={{ path: { stroke: "white" } }}
            onClick={() => onUrl("")}
          >
            <GrFormClose />
          </ActionIcon>
        </Group>
      )}
    </Box>
  );
};
